/**
 * Wave 3.4 (#3974928e) — full hypothetical-load chain assembly for precompiled
 * progressions. Composes engine primitives (Wave 1.2 / 2.4 / 3.2) with the
 * precompiled-shape adapters from #d646de3c / #ef46a4be / #583bd67b to produce
 * a flat list of `Projection` entries describing every relevant downstream
 * projection for a given (weekIdx, setIdx).
 *
 * Sits OUTSIDE `progressionEngine.ts` to keep the engine pure (engine knows
 * SetItem; this file knows PrecompiledSet/PrecompiledWeek).
 *
 * applyForNext walk-back is OUT OF SCOPE for this wave per the task spec —
 * the chain operates on the immediate set at (weekIdx, setIdx) only.
 */
import type {
  PrecompiledSet,
  PrecompiledWeek,
  PrecompiledWeekProgressionRule,
  SetItem,
  SogliaItem,
} from "../types/Entities";
import {
  computeMiniSetLoads,
  computeNextSetInSetLoad,
  computeNextSetInSetReps,
  computeNextWeekLoad,
  computeNextWeekReps,
  computeNextWeekXRepsLoad,
} from "./progressionEngine";
import {
  applyRuleStS,
  applyRuleSiS,
  applyRuleStSReps,
  applyRuleSiSReps,
  repsThresholdsToSoglie,
  resolveSourceSlot,
  ruleHasInheritableBody,
  thresholdsToSoglie,
} from "./precompiledRuleApply";

export type ChainProjection = {
  kind: "main" | "xReps" | "miniSet";
  label: string;
  reps?: number | string;
  load?: number;
  appliedFrom: { weekIdx: number; setIdx: number };
};

// PROG-INT/B (#a37befa5) — `thresholdsToSoglie` moved to
// `src/utils/precompiledRuleApply.ts` so the Programmazione adapter can
// reuse it. FE-H (#130311b7) — `repsThresholdsToSoglie` moved likewise.
// Both imported above.

/**
 * Build a synthetic SetItem from a precompiled set + week, suitable for the
 * engine's compute* functions. `target` selects which side of the precompiled
 * rule we feed to the engine: "load" emits load-typed soglie from `loadValue`,
 * "reps" emits repScheme-typed soglie from `repsValue`. simulateChain calls
 * this once per active target so a `incrementTarget: "both"` rule produces
 * BOTH load and reps projections in the same chain.
 */
function buildSyntheticSetItem(
  prevSet: PrecompiledSet,
  weekRepScheme: string | null | undefined,
  target: "load" | "reps" = "load",
): SetItem {
  const rule = prevSet.progressionRule;
  const lctStS: "%" | "weight" = rule.loadChangeType === "kg" ? "weight" : "%";
  const lctSiS: "%" | "weight" = rule.loadChangeTypeSiS === "kg" ? "weight" : "%";
  const stSType: "%" | "weight" | "repScheme" = target === "reps" ? "repScheme" : lctStS;
  const siSType: "%" | "weight" | "repScheme" = target === "reps" ? "repScheme" : lctSiS;
  const mkSoglie = (
    side: "stsPos" | "stsNeg" | "sisPos" | "sisNeg",
  ): SogliaItem[] => {
    const arr =
      side === "stsPos" ? rule.positiveThresholdsStS
      : side === "stsNeg" ? rule.negativeThresholdsStS
      : side === "sisPos" ? rule.positiveThresholdsSiS
      : rule.negativeThresholdsSiS;
    if (target === "reps") return repsThresholdsToSoglie(arr);
    const lt = side.startsWith("sts") ? lctStS : lctSiS;
    return thresholdsToSoglie(arr, lt);
  };
  return {
    intensityType: "%",
    progressionType: rule.progressionType ?? "set-to-set",
    calculationPriority: "soglie",
    workLoad: prevSet.workLoad != null ? String(prevSet.workLoad) : undefined,
    workReps: prevSet.workReps ?? undefined,
    repScheme: (prevSet.repScheme ?? weekRepScheme) ?? undefined,
    incrementRuleStS: { type: stSType, value: 0, soglie: mkSoglie("stsPos") },
    decrementRuleStS: { type: stSType, value: 0, soglie: mkSoglie("stsNeg") },
    incrementRuleSiS: { type: siSType, value: 0, soglie: mkSoglie("sisPos") },
    decrementRuleSiS: { type: siSType, value: 0, soglie: mkSoglie("sisNeg") },
  };
}

export interface SimulateChainOptions {
  /**
   * The Tecnica catalog used to gate X-reps and mini-set projections.
   * Format: each entry has `id?`, `name`, `includeXReps?`, `dropXReps?`.
   */
  tecnicheRaw?: { id?: number; name: string; includeXReps?: boolean; dropXReps?: boolean }[];
}

/**
 * Returns the full hypothetical-load chain for the set at (weekIdx, setIdx).
 * Order: main StS → main SiS → main reps StS/SiS → X-reps load → mini-sets.
 * Returns [] if the set is missing or no projections fire.
 */
export function simulateChain(
  weeks: PrecompiledWeek[],
  weekIdx: number,
  setIdx: number,
  opts: SimulateChainOptions = {},
): ChainProjection[] {
  const week = weeks[weekIdx];
  if (!week) return [];
  const set = week.sets?.[setIdx];
  if (!set) return [];
  // Defensive guard (FE-R2 §4b, softened in #d47d1445): precompiledChain
  // operates on PrecompiledSet from the SOURCE progression. The
  // `precompiledSource` envelope is type-only today — its integration
  // (override-aware chain rendering for SetItems) is deferred to a future
  // architect-scoped task (#fd17ef2a). If a SetItem carrying that envelope
  // reaches this seam today, treat it as a no-op rather than throwing:
  // dev-mode `console.warn` surfaces the routing anomaly without breaking
  // any caller that happens to thread the wrong shape through.
  if ("precompiledSource" in set && (set as Record<string, unknown>)["precompiledSource"] != null) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        "[precompiledChain] simulateChain received a SetItem with precompiledSource. " +
          "Returning [] — override-aware chain rendering is deferred (see #fd17ef2a).",
      );
    }
    return [];
  }
  const out: ChainProjection[] = [];
  const at = { weekIdx, setIdx };
  // PROG-ENGINE-V3/1 — when the slot's own rule lacks an inheritable body
  // (no progressionType/thresholds/weekPriority/initialLoad — only a bare
  // `loadComputeSource` pick, or nothing at all), pull the body from
  // walk-back so the chain projects against the source-axis rule. STS and
  // SIS resolve independently. When the own rule explicitly picks a single
  // axis via `loadComputeSource`, honor that pick; otherwise infer from
  // which axes resolved (legacy ENGINE-V2 behavior for fully-empty slots).
  let rule: PrecompiledWeekProgressionRule = set.progressionRule;
  if (!ruleHasInheritableBody(rule)) {
    const stsRes = resolveSourceSlot(weeks, weekIdx, setIdx, "sts", rule);
    const sisRes = resolveSourceSlot(weeks, weekIdx, setIdx, "sis", rule);
    const stsBody = stsRes && ruleHasInheritableBody(stsRes.rule) ? stsRes.rule : null;
    const sisBody = sisRes && ruleHasInheritableBody(sisRes.rule) ? sisRes.rule : null;
    if (stsBody || sisBody) {
      const base = stsBody ?? sisBody ?? rule;
      const synth: PrecompiledWeekProgressionRule = { ...base };
      if (rule.loadComputeSource != null) {
        synth.loadComputeSource = rule.loadComputeSource;
      } else if (stsBody && sisBody) {
        synth.loadComputeSource = "both";
      } else if (stsBody) {
        synth.loadComputeSource = "sts";
      } else if (sisBody) {
        synth.loadComputeSource = "sis";
      }
      rule = synth;
    }
  }
  const wr = set.workReps != null ? parseFloat(String(set.workReps)) : NaN;
  const wl = set.workLoad != null ? Number(set.workLoad) : NaN;
  const hasWork = !isNaN(wr) && !isNaN(wl) && wr > 0 && wl > 0;

  // Main projections — fire per-target on either threshold rule or fixed
  // (fixed_increment / wn_rm). FE-A (#a4c1a4b4): the legacy gate only handled
  // the threshold path, which silently dropped any SiS projection when the
  // user picked "Progressione Fissa" with fixed_increment or wn_rm.
  if (hasWork) {
    const pt = rule.progressionType ?? null;
    const stsKind = rule.type;
    const sisKind = rule.typeSiS ?? rule.type;
    const wpStS = rule.weekPriority;
    const wpSiS = rule.weekPrioritySiS;
    const isStSFixed =
      stsKind === "fixed" && (wpStS === "fixed_increment" || wpStS === "wn_rm");
    const isSiSFixed =
      sisKind === "fixed" && (wpSiS === "fixed_increment" || wpSiS === "wn_rm");
    const stsActive =
      (pt === "set-to-set" || pt === "both") &&
      (stsKind === "thresholds" || isStSFixed);
    const sisActive =
      (pt === "set-in-set" || pt === "both") &&
      (sisKind === "thresholds" || isSiSFixed);

    // FE-2 (#bf6553b3) — incrementTarget gates which engine paths fire. "load"
    // → load fns only, "reps" → reps fns with repScheme-typed soglie, "both"
    // → both. Default "load" preserves legacy behavior when the field is unset.
    const stsTarget = rule.incrementTarget ?? "load";
    const sisTarget = rule.incrementTargetSiS ?? "load";
    const stsWantsLoad = stsTarget === "load" || stsTarget === "both";
    const stsWantsReps = stsTarget === "reps" || stsTarget === "both";
    const sisWantsLoad = sisTarget === "load" || sisTarget === "both";
    const sisWantsReps = sisTarget === "reps" || sisTarget === "both";

    // Threshold-path synthetic items — only built when at least one threshold
    // target on either side is active.
    const stsThr = stsActive && stsKind === "thresholds";
    const sisThr = sisActive && sisKind === "thresholds";
    const synthLoad = ((stsThr && stsWantsLoad) || (sisThr && sisWantsLoad))
      ? buildSyntheticSetItem(set, week.repScheme, "load")
      : null;
    const synthReps = ((stsThr && stsWantsReps) || (sisThr && sisWantsReps))
      ? buildSyntheticSetItem(set, week.repScheme, "reps")
      : null;

    // #af841a27 — additive out-of-threshold fallback. When the engine returns
    // undefined for a thresholds branch (workReps outside ALL bands), surface a
    // chain entry carrying the source set's trainer-initial values instead of
    // silently dropping it. Load axis → set.trialLoad. Reps axis →
    // set.workReps ?? set.repScheme ?? week.repScheme. Fixed / fixed_increment /
    // wn_rm branches untouched. applyRuleThresholdInherit /
    // applyRuleThresholdInheritReps keep their existing PROG-BUGS/ENGINE-1
    // (Bug C) and PROG-ENGINE-V2/2 (Bug 2) semantics — this fallback is scoped
    // to the chain entry only, per architect's resolution on #82c957c1.
    const fallbackReps = (): number | string | undefined => {
      if (set.workReps != null && String(set.workReps) !== "") return set.workReps;
      if (set.repScheme != null && set.repScheme !== "") return set.repScheme;
      if (week.repScheme != null && week.repScheme !== "") return week.repScheme;
      return undefined;
    };
    if (stsActive) {
      if (stsWantsLoad) {
        let stsLoad: number | undefined;
        if (stsKind === "thresholds" && synthLoad) {
          stsLoad = computeNextWeekLoad(synthLoad, wr);
          if (stsLoad === undefined && set.trialLoad != null) {
            stsLoad = set.trialLoad;
          }
        } else if (isStSFixed) {
          const v = applyRuleStS(wl, rule);
          if (v !== null) stsLoad = v;
        }
        if (stsLoad !== undefined) {
          out.push({ kind: "main", label: "Sett. succ.", load: stsLoad, appliedFrom: at });
        }
      }
      if (stsWantsReps) {
        if (stsKind === "thresholds" && synthReps) {
          const stsReps = computeNextWeekReps(synthReps, wr);
          if (stsReps?.kind === "reps") {
            out.push({ kind: "main", label: "Sett. succ. (reps)", reps: stsReps.reps, appliedFrom: at });
          } else if (stsReps?.kind === "scheme") {
            out.push({ kind: "main", label: "Sett. succ. (reps)", reps: stsReps.repScheme, appliedFrom: at });
          } else {
            const fb = fallbackReps();
            if (fb !== undefined) {
              out.push({ kind: "main", label: "Sett. succ. (reps)", reps: fb, appliedFrom: at });
            }
          }
        } else if (isStSFixed) {
          const r = applyRuleStSReps(wr, rule);
          if (r !== null) {
            out.push({ kind: "main", label: "Sett. succ. (reps)", reps: r, appliedFrom: at });
          }
        }
      }
    }
    if (sisActive) {
      if (sisWantsLoad) {
        let sisLoad: number | undefined;
        if (sisKind === "thresholds" && synthLoad) {
          sisLoad = computeNextSetInSetLoad(synthLoad, wr);
          if (sisLoad === undefined && set.trialLoad != null) {
            sisLoad = set.trialLoad;
          }
        } else if (isSiSFixed) {
          const v = applyRuleSiS(wl, rule);
          if (v !== null) sisLoad = v;
        }
        if (sisLoad !== undefined) {
          out.push({ kind: "main", label: "Set succ.", load: sisLoad, appliedFrom: at });
        }
      }
      if (sisWantsReps) {
        if (sisKind === "thresholds" && synthReps) {
          const sisReps = computeNextSetInSetReps(synthReps, wr);
          if (sisReps?.kind === "reps") {
            out.push({ kind: "main", label: "Set succ. (reps)", reps: sisReps.reps, appliedFrom: at });
          } else if (sisReps?.kind === "scheme") {
            out.push({ kind: "main", label: "Set succ. (reps)", reps: sisReps.repScheme, appliedFrom: at });
          } else {
            const fb = fallbackReps();
            if (fb !== undefined) {
              out.push({ kind: "main", label: "Set succ. (reps)", reps: fb, appliedFrom: at });
            }
          }
        } else if (isSiSFixed) {
          const r = applyRuleSiSReps(wr, rule);
          if (r !== null) {
            out.push({ kind: "main", label: "Set succ. (reps)", reps: r, appliedFrom: at });
          }
        }
      }
    }
  }

  // X-reps load — gated on Tecnica.includeXReps. Only fires when the engine's
  // computeNextWeekXRepsLoad has all inputs (workXReps, workXLoad, soglieXReps,
  // xReps target). Precompiled doesn't currently store these as set-level fields,
  // so this branch only fires if a future migration starts populating them on
  // the synthesized SetItem. Kept here for completeness; non-firing is silent.
  const tecName = set.tecnica;
  const tec = (opts.tecnicheRaw ?? []).find(
    (t) => t.name === tecName || (tecName != null && String(t.id) === String(tecName)),
  );
  if (tec?.includeXReps && hasWork) {
    // Build a minimal SetItem with X-reps-shaped fields if the precompiled
    // record carried them. Today PrecompiledSet doesn't have workXReps/xReps,
    // so xRepsLoad will be undefined; we still expose the branch as a no-op
    // so future schema additions activate it without code change.
    const synth = buildSyntheticSetItem(set, week.repScheme, "load");
    const xLoad = computeNextWeekXRepsLoad(synth, wr);
    if (xLoad !== undefined) {
      out.push({ kind: "xReps", label: "X-reps", load: xLoad, appliedFrom: at });
    }
  }

  // Mini-sets cascade — gated on miniSets being present. Tecnica-gating is
  // already enforced by the form (only shows the editor when includeXReps &&
  // dropXReps); here we just project whatever was persisted.
  if (set.miniSets && set.miniSets.length > 0) {
    const baseLoad =
      (set.workLoad != null ? Number(set.workLoad) : 0) ||
      (set.trialLoad != null ? Number(set.trialLoad) : 0);
    const projections = computeMiniSetLoads(baseLoad, set.miniSets);
    projections.forEach((p, i) => {
      out.push({
        kind: "miniSet",
        label: `Mini #${i + 1}`,
        reps: p.reps,
        load: p.load,
        appliedFrom: at,
      });
    });
  }

  return out;
}
